import { create } from 'zustand';
import { getOrders } from '../../services/orderApi.js';

export const useOrderStore = create((set, get) => ({
  orders: [],
  loading: false,
  loadingMore: false,
  error: null,
  lastFetched: null,
  ordersHasLoaded: false,
  start: 0,
  end: 100,
  hasMoreOrders: true,

  setOrders: (orders) => set({ orders, lastFetched: Date.now() }),
  setLoading: (loading) => set({ loading }),
  setLoadingMore: (loadingMore) => set({ loadingMore }),
  setError: (error) => set({ error }),
  setOrderPaginationRange: (start, end) => set({ start, end }),
  setHasMoreOrders: (hasMore) => set({ hasMoreOrders: hasMore }),

  // Initialize orders on first load with a limited batch
  initializeOrders: async (forceReload = false) => {
    const state = get();
    if (state.loading || state.loadingMore) {
      return state.orders;
    }
    if (!forceReload && state.ordersHasLoaded) {
      return state.orders;
    }

    const initialStart = 0;
    const initialEnd = 100;

    set({ loading: true, error: null, start: initialStart, end: initialEnd, hasMoreOrders: true });
    try {
      const ordersResponse = await getOrders({ start: initialStart, end: initialEnd });
      const orders = (ordersResponse.orders || []).map((order) => ({
        ...order,
        total: Number(order.total) || 0,
        subtotal: Number(order.subtotal) || 0,
        discount: Number(order.discount) || 0,
        tax_amount: Number(order.tax_amount) || 0,
        amount_paid: Number(order.amount_paid) || 0,
        change_given: Number(order.change_given) || 0,
      }));
      set({
        orders,
        ordersHasLoaded: true,
        lastFetched: Date.now(),
        loading: false,
        loadingMore: false,
        error: null,
        start: initialStart,
        end: initialEnd,
        hasMoreOrders: orders.length === 100
      });
      return orders;
    } catch (err) {
      set({
        error: err.message || 'Unable to load orders',
        loading: false,
        loadingMore: false
      });
      throw err;
    }
  },

  loadNextOrdersBatch: async () => {
    const state = get();
    if (state.loading || state.loadingMore || !state.hasMoreOrders) {
      return [];
    }

    const nextStart = state.end;
    const nextEnd = state.end + 100;
    set({ loadingMore: true, error: null });

    try {
      const ordersResponse = await getOrders({ start: nextStart, end: nextEnd });
      const nextOrders = (ordersResponse.orders || []).map((order) => ({
        ...order,
        total: Number(order.total) || 0,
        subtotal: Number(order.subtotal) || 0,
        discount: Number(order.discount) || 0,
        tax_amount: Number(order.tax_amount) || 0,
        amount_paid: Number(order.amount_paid) || 0,
        change_given: Number(order.change_given) || 0,
      }));
      set((prevState) => ({
        orders: [...prevState.orders, ...nextOrders],
        lastFetched: Date.now(),
        loadingMore: false,
        error: null,
        start: prevState.start,
        end: nextEnd,
        hasMoreOrders: nextOrders.length === 100
      }));
      return nextOrders;
    } catch (err) {
      set({ error: err.message || 'Unable to load more orders', loadingMore: false });
      throw err;
    }
  },

  // Update a single order in the store
  updateOrderStatus: (orderId, updatedOrder) => {
    const parsedOrderId = Number(orderId);
    set((state) => ({
      orders: state.orders.map((order) =>
        order.id === parsedOrderId ? { ...order, ...updatedOrder } : order
      )
    }));
  },

  // Update multiple orders at once (for batch updates)
  updateOrders: (updatedOrders) => {
    set((state) => {
      const orderMap = new Map(state.orders.map((o) => [o.id, o]));
      updatedOrders.forEach((updatedOrder) => {
        if (orderMap.has(updatedOrder.id)) {
          orderMap.set(updatedOrder.id, {
            ...orderMap.get(updatedOrder.id),
            ...updatedOrder
          });
        }
      });
      return { orders: Array.from(orderMap.values()) };
    });
  },

  // Add a single new order to the beginning of the list
  addOrder: (newOrder) => {
    set((state) => ({
      orders: [newOrder, ...state.orders]
    }));
  },

  // Legacy methods for backward compatibility
  loadOrders: async (forceReload = false) => {
    return get().initializeOrders(forceReload);
  },

  refreshOrders: async () => get().initializeOrders(true),
  clearOrders: () => set({ orders: [], lastFetched: null, error: null, ordersHasLoaded: false, start: 0, end: 100, hasMoreOrders: true })
}));

export default useOrderStore;
